import React, { useState } from "react";
import {
  BulbOutlined,
  CheckCircleOutlined,
  CodeOutlined,
  DownOutlined,
  LoadingOutlined,
  MessageOutlined,
  NumberOutlined,
  RightOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import type { DataQuestionStep } from "../../store/api/dataQuestionsApi";
import styles from "./styles.module.css";

interface TraceStepsProps {
  steps: DataQuestionStep[];
  /** While the agent is still working: expanded, last step highlighted. */
  live?: boolean;
}

const ICONS: Record<DataQuestionStep["kind"], React.ReactNode> = {
  thinking: <BulbOutlined />,
  sql: <CodeOutlined />,
  executed: <CheckCircleOutlined />,
  retry: <WarningOutlined />,
  count: <NumberOutlined />,
  answer: <MessageOutlined />,
};

/**
 * The agent's visible reasoning: what it queried, what failed, what it fixed.
 * Collapsed by default on finished turns (the answer comes first for a
 * non-technical reader) and open while a question is in flight.
 */
export const TraceSteps: React.FC<TraceStepsProps> = ({ steps, live = false }) => {
  const [open, setOpen] = useState(false);
  const [sqlOpen, setSqlOpen] = useState<Record<number, boolean>>({});
  const expanded = live || open;
  const lastIndex = steps.length - 1;

  return (
    <div className={styles.trace} data-testid="trace">
      <button
        type="button"
        className={styles.traceToggle}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={expanded}
      >
        {live ? <LoadingOutlined /> : expanded ? <DownOutlined /> : <RightOutlined />}
        {live
          ? "Resolviendo la pregunta…"
          : `Cómo lo resolví · ${steps.length} ${steps.length === 1 ? "paso" : "pasos"}`}
      </button>
      {expanded && (
        <ol className={styles.traceList}>
          {steps.map((step, index) => {
            const isLast = live && index === lastIndex;
            const hasSql = step.kind === "sql" || step.kind === "executed";
            return (
              <li
                key={`${step.at}-${index}`}
                className={`${styles.traceStep} ${
                  step.kind === "retry" ? styles.traceRetry : ""
                } ${isLast ? styles.traceLive : ""}`}
              >
                <span className={styles.traceIcon}>
                  {isLast ? <LoadingOutlined /> : ICONS[step.kind]}
                </span>
                <span>
                  {step.text}
                  {step.kind === "retry" && (
                    <span className={styles.traceError}>{step.error}</span>
                  )}
                  {hasSql && (
                    <>
                      {" "}
                      <button
                        type="button"
                        className={styles.inlineToggle}
                        onClick={() =>
                          setSqlOpen((value) => ({ ...value, [index]: !value[index] }))
                        }
                      >
                        {sqlOpen[index] ? "ocultar SQL" : "ver SQL"}
                      </button>
                      {sqlOpen[index] && <pre className={styles.sql}>{step.sql}</pre>}
                    </>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
